import type { IBusinessUnit, IBusinessUnitManager, IManagerOrderItem } from 'src/types/business-unit';

import { useMemo } from 'react';
import useSWR from 'swr';

import { endpoints, fetcher } from 'src/utils/axios';


const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};


type BusinessUnitsData = {
  data: IBusinessUnit[];
  meta: {
    page: number;
    take: number;
    itemCount: number;
    pageCount: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
};



export function useGetBusinessUnits(page: number = 1, take: number = 10, search: string = '') {
  const queryParams = new URLSearchParams({
    page: page.toString(),
    take: take.toString(),
  });

  if (search) {
    queryParams.append('search', search);
  }

  const url = `${endpoints.businessUnits.list}?${queryParams.toString()}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR<BusinessUnitsData>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      businessUnits: data?.data || [],
      meta: data?.meta,
      businessUnitsLoading: isLoading,
      businessUnitsError: error,
      businessUnitsValidating: isValidating,
      businessUnitsEmpty: !isLoading && !data?.data?.length,
      mutateBusinessUnits: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}


export function useGetBusinessUnit(id: string) {
  const url = id ? endpoints.businessUnits.details(id) : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IBusinessUnit>(
    url,
    fetcher,
    swrOptions
  );

  const memoizedValue = useMemo(
    () => ({
      businessUnit: data,
      businessUnitLoading: isLoading,
      businessUnitError: error,
      businessUnitValidating: isValidating,
      mutateBusinessUnit: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}



export function useGetBusinessUnitsForSelection() {
  const url = `${endpoints.businessUnits.list}?page=1&take=300&forSelection=true`;

  const { data, isLoading, error, isValidating, mutate } = useSWR<BusinessUnitsData>(
    url,
    fetcher,
    { ...swrOptions, revalidateOnMount: true }
  );

  const memoizedValue = useMemo(
    () => ({
      businessUnitOptions: data?.data?.map(unit => ({ id: unit.id, name: unit.name })) || [],
      businessUnitOptionsLoading: isLoading,
      businessUnitOptionsError: error,
      businessUnitOptionsValidating: isValidating,
      mutateBusinessUnitOptions: mutate,
    }),
    [data?.data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}


export async function createBusinessUnit(businessUnitData: {
  name: string;
  description?: string;
}): Promise<IBusinessUnit> {
  const response = await fetcher<IBusinessUnit>(
    endpoints.businessUnits.create,
    'POST',
    {
      data: businessUnitData,
    }
  );
  return response;
}



export async function updateBusinessUnit(
  id: number | string,
  businessUnitData: {
    name?: string;
    description?: string;
  }
): Promise<IBusinessUnit> {
  const response = await fetcher<IBusinessUnit>(
    endpoints.businessUnits.update(id),
    'PUT',
    {
      data: businessUnitData,
    }
  );
  return response;
}



export async function deleteBusinessUnit(id: number | string): Promise<void> {
  await fetcher<void>(
    endpoints.businessUnits.delete(id),
    'DELETE'
  );
}

export async function getBusinessUnitManagers(id: number | string): Promise<IBusinessUnitManager[]> {
  const response = await fetcher<IBusinessUnitManager[]>(
    endpoints.businessUnits.managers(id),
    'GET'
  );
  return response;
}

export async function updateBusinessUnitManagersOrder(
  id: number | string,
  managers: IManagerOrderItem[]
): Promise<IBusinessUnitManager[]> {
  const managerUserIds = [...managers]
    .sort((a, b) => a.order - b.order)
    .map((manager) => manager.userId);

  const response = await fetcher<IBusinessUnitManager[]>(
    endpoints.businessUnits.updateManagersOrder(id),
    'PUT',
    {
      data: { managerUserIds },
    }
  );
  return response;
}